// Task #331 — NZ phone-number normalisation for the Search page.
//
// Accepts the common NZ-mobile formats users actually type and resolves
// each to the same E.164 representation so the server search and
// call-history endpoints can look up by exact phone number.
//
// Recognised input shapes (any spacing / dashes / brackets allowed):
//   +64212173145, +64 21 217 3145   → e164: "+64212173145"
//   0212173145, 021 217 3145        → e164: "+64212173145"
//   64212173145                     → e164: "+64212173145"
//   2173145                         → suffix-only (server suffix match)
//
// When the input doesn't fit any of these shapes (too short, only
// punctuation, etc) the helper returns ``kind: "invalid"`` so the UI
// can surface "No records found" without firing a server call.

export type NormalizedPhone =
  | { kind: "e164"; value: string }
  | { kind: "suffix"; value: string }
  | { kind: "invalid" };

const E164_MIN_DIGITS = 9; // +64 + at least 7 local digits
const E164_MAX_DIGITS = 15;
const SUFFIX_MIN_DIGITS = 6;
const SUFFIX_MAX_DIGITS = 9;

export function normalizePhone(raw: string): NormalizedPhone {
  if (typeof raw !== "string") return { kind: "invalid" };
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "invalid" };

  const hasPlus = trimmed.startsWith("+");
  // Strip spaces, dashes, brackets, dots, and any other punctuation —
  // keep only digits. The leading "+" is preserved separately above.
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return { kind: "invalid" };

  // International form. Accept "+64..." or "+<other country>..." but the
  // backend's search runs against the calls table, so any non-NZ E.164
  // is just an exact lookup — return it as-is.
  if (hasPlus) {
    const e164 = "+" + digits;
    if (digits.length < E164_MIN_DIGITS || digits.length > E164_MAX_DIGITS) {
      return { kind: "invalid" };
    }
    return { kind: "e164", value: e164 };
  }

  // NZ mobile in international form without the plus: "64212173145".
  if (
    digits.startsWith("64") &&
    digits.length >= E164_MIN_DIGITS &&
    digits.length <= E164_MAX_DIGITS
  ) {
    return { kind: "e164", value: "+" + digits };
  }

  // National form starting with 0: "0212173145" → "+64212173145".
  // The leading 0 is dropped and "64" prepended (net +1 digit), so cap the
  // input one short of E164_MAX_DIGITS to keep the result within E.164.
  if (digits.startsWith("0") && digits.length >= 8 && digits.length <= E164_MAX_DIGITS - 1) {
    return { kind: "e164", value: "+64" + digits.slice(1) };
  }

  // Local-suffix-only: "2173145". Server does a suffix/substring match.
  if (digits.length >= SUFFIX_MIN_DIGITS && digits.length <= SUFFIX_MAX_DIGITS) {
    return { kind: "suffix", value: digits };
  }

  return { kind: "invalid" };
}
